import clsx from './clsx';
import styles from './VoiceCapture.module.css';

// =============================================================================
// VoiceCapture — the big mic-toggle capture surface (LANE DNA-BUILD, Phase 1).
// Re-skinned port of apps/web/src/components/corpus/VoiceCapture.tsx: same
// idle/recording/transcribing/error state machine, Tailwind classes swapped
// for --tl-* tokens per the plan's re-skin discipline
// (docs/design/dna-interview-port-2026-07-05.md §3 Phase 1, "a clean,
// dependency-light state machine").
// =============================================================================

export type VoiceCaptureState = 'idle' | 'recording' | 'transcribing' | 'error';

export interface VoiceCaptureProps {
  state: VoiceCaptureState;
  prompt: string;
  onToggle?: () => void;
  durationSeconds?: number;
}

export function VoiceCapture({ state, prompt, onToggle, durationSeconds = 0 }: VoiceCaptureProps) {
  const isRecording = state === 'recording';
  const isBusy = state === 'transcribing';

  return (
    <div className={styles.card}>
      <p className={styles.prompt}>{prompt}</p>

      <button
        type="button"
        onClick={onToggle}
        aria-label={isRecording ? 'Stop recording' : 'Start recording'}
        disabled={isBusy}
        className={clsx(
          styles.micButton,
          isRecording && styles.micButtonRecording,
          isBusy && styles.micButtonBusy,
        )}
      >
        {isRecording ? (
          <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <rect x="6" y="6" width="12" height="12" rx="2" />
          </svg>
        ) : (
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect
              x="9"
              y="3"
              width="6"
              height="11"
              rx="3"
              stroke="currentColor"
              strokeWidth="1.9"
            />
            <path
              d="M5 11a7 7 0 0 0 14 0M12 18v3"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
            />
          </svg>
        )}
      </button>

      <div className={styles.statusRow}>
        {isRecording ? (
          <>
            <span className={styles.recordingDot} />
            Recording — {formatTime(durationSeconds)}
          </>
        ) : null}
        {isBusy ? <span>Transcribing…</span> : null}
        {state === 'idle' ? <span>Tap to begin</span> : null}
        {state === 'error' ? <span className={styles.error}>Microphone unavailable</span> : null}
      </div>
    </div>
  );
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}
